import React, { useState, useEffect, useRef } from 'react';
import { E, T, usePortfolio, Position } from '../../logic/imports';

interface NewOrderSelectProps {
    instrument: T.Instrument;
    onDimPosition: (enable: boolean) => void;
    onOrderTypeSelected?: (side: E.OrderSide, type: E.OrderType) => void;
}

const BUY_OPTIONS: E.OrderType[] = [
    E.OrderType.MARKET,
    E.OrderType.FLEX
];

const SELL_OPTIONS: E.OrderType[] = [
    E.OrderType.MARKET,
    E.OrderType.FLEX
];

const ORDER_TYPE_LABELS: Record<E.OrderType, string> = {
    [E.OrderType.MARKET]: 'Market',
    [E.OrderType.FLEX]: 'Flex'
};

export const NewOrderSelect: React.FC<NewOrderSelectProps> = (props) => {
    const { instrument, onDimPosition, onOrderTypeSelected } = props;
    const { positions } = usePortfolio();
    const position = Array.from(positions.values()).find(
        (pos: Position) => pos.option.symbol === instrument.symbol
    );
    const [openMenu, setOpenMenu] = useState<'buy' | 'sell' | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Sync dimmer with menu state
    useEffect(() => {
        onDimPosition(openMenu !== null);
    }, [openMenu, onDimPosition]);

    // Close menu on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpenMenu(null);
            }
        };

        if (openMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [openMenu]);

    const handleOrderTypeSelected = (type: E.OrderType) => {
        setOpenMenu(null);
        onOrderTypeSelected?.(
            openMenu === 'buy' ? E.OrderSide.BUY : E.OrderSide.SELL,
            type
        );
    };

    const handleBuyClick = () => {
        setOpenMenu(openMenu === 'buy' ? null : 'buy');
    };

    const handleSellClick = () => {
        setOpenMenu(openMenu === 'sell' ? null : 'sell');
    };

    const renderDropdownMenu = (options: E.OrderType[], isOpen: boolean) => {
        if (!isOpen) return null;

        return (
            <div className="dropdown-menu">
                {options.map(option => (
                    <div
                        key={option}
                        className="dropdown-option"
                        onClick={() => handleOrderTypeSelected(option)}
                    >
                        {ORDER_TYPE_LABELS[option]}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div ref={containerRef}>
            <div className="dropdown-container">

                {/* Buy Button - always show */}
                <div style={{ position: 'relative', minWidth: '140px' }}>
                    <button
                        className="button"
                        style={{ width: '200px', height: '25px', padding: '0px' }}
                        onClick={handleBuyClick}
                    >
                        Buy
                    </button>
                    {renderDropdownMenu(BUY_OPTIONS, openMenu === 'buy')}
                </div>

                {/* Sell Button - only show if there are available contracts */}
                <div style={{ position: 'relative', minWidth: '140px' }}>
                    <button
                        className="button"
                        style={{ width: '200px', height: '25px', padding: '0px' }}
                        onClick={handleSellClick}
                        disabled={!position || position.contractsAvailable < 1}
                    >
                        Sell
                    </button>
                    {renderDropdownMenu(SELL_OPTIONS, openMenu === 'sell')}
                </div>
                
            </div>
        </div>
    );
}